/**
 * Hooks de KPIs totalizadores para as listagens do painel White Label.
 * Cada hook chama uma RPC `wl_get_*_kpis` (independente da paginação),
 * respeitando os filtros aplicados na tela.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-28 v1.0.0 - Criação dos 3 hooks (Tenants, Instâncias, Usuários).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** KPIs de Tenants. */
export interface WlTenantKpis {
  total: number;
  active: number;
  suspended: number;
  trial: number;
  cancelled: number;
  created_today: number;
  courtesy: number;
}

/** KPIs de Instâncias de Provider. */
export interface WlInstanceKpis {
  total: number;
  in_use: number;
  pending: number;
  released: number;
  cancelled: number;
  created_today: number;
}

/** KPIs de Usuários (membros de tenants). */
export interface WlTenantUserKpis {
  total: number;
  active: number;
  inactive: number;
  owners: number;
  joined_today: number;
}

const ZERO_TENANT: WlTenantKpis = { total: 0, active: 0, suspended: 0, trial: 0, cancelled: 0, created_today: 0, courtesy: 0 };
const ZERO_INSTANCE: WlInstanceKpis = { total: 0, in_use: 0, pending: 0, released: 0, cancelled: 0, created_today: 0 };
const ZERO_USER: WlTenantUserKpis = { total: 0, active: 0, inactive: 0, owners: 0, joined_today: 0 };

/** Normaliza a primeira linha (RPC retorna RETURNS TABLE → array). */
function firstRow<T>(data: any, fallback: T): T {
  if (!data) return fallback;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return fallback;
  // Converte BIGINTs (vêm como string) para Number.
  const out: any = {};
  Object.keys(fallback as any).forEach((k) => {
    out[k] = Number(row[k] ?? 0);
  });
  return out as T;
}

/** KPIs de Tenants. */
export function useWlTenantKpis(filters: {
  search?: string | null;
  status?: string | null;
  plan?: string | null;
  created_from?: string | null;
  created_to?: string | null;
  // null = sem filtro; true = somente cortesia; false = sem cortesia
  is_courtesy?: boolean | null;
}) {
  return useQuery({
    queryKey: ['wl-tenant-kpis', filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('wl_get_tenant_kpis', {
        p_search: filters.search || null,
        p_status: filters.status || null,
        p_plan: filters.plan || null,
        p_created_from: filters.created_from || null,
        p_created_to: filters.created_to || null,
        p_is_courtesy:
          filters.is_courtesy === null || filters.is_courtesy === undefined
            ? null
            : filters.is_courtesy,
      } as never);
      if (error) throw error;
      return firstRow<WlTenantKpis>(data, ZERO_TENANT);
    },
    staleTime: 15_000,
  });
}


/** KPIs de Instâncias. */
export function useWlInstanceKpis(filters: {
  tenant_id?: string | null;
  provider_id?: string | null;
  status?: string | null;
  source_type?: string | null;
  search?: string | null;
}) {
  return useQuery({
    queryKey: ['wl-instance-kpis', filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('wl_get_provider_instance_kpis', {
        p_tenant_id: filters.tenant_id || null,
        p_provider_id: filters.provider_id || null,
        p_status: filters.status || null,
        p_source_type: filters.source_type || null,
        p_search: filters.search || null,
      });
      if (error) throw error;
      return firstRow<WlInstanceKpis>(data, ZERO_INSTANCE);
    },
    staleTime: 15_000,
  });
}

/** KPIs de Usuários dos Tenants. */
export function useWlTenantUserKpis(filters: {
  tenant_id?: string | null;
  search?: string | null;
  is_active?: boolean | null;
}) {
  return useQuery({
    queryKey: ['wl-tenant-user-kpis', filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('wl_get_tenant_user_kpis', {
        p_tenant_id: filters.tenant_id || null,
        p_search: filters.search || null,
        p_is_active: filters.is_active === null || filters.is_active === undefined ? null : filters.is_active,
      });
      if (error) throw error;
      return firstRow<WlTenantUserKpis>(data, ZERO_USER);
    },
    staleTime: 15_000,
  });
}
